import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import PortalLayout from "@/layouts/PortalLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Rocket, Plus, Edit3, Trash2, X, Eye, Users, ExternalLink, FileText, Inbox, CheckCircle2, XCircle } from "lucide-react";
import MarkdownEditor from "@/components/MarkdownEditor";
import ImageUpload from "@/components/ImageUpload";

const CATEGORY_OPTIONS = [
  { value: "financements", label: "💰 Financements" },
  { value: "acceleration", label: "🚀 Accélération" },
  { value: "challenges", label: "🏆 Challenges" },
  { value: "international", label: "🌍 International" },
  { value: "appels_offres", label: "📋 Appels d'offres" },
  { value: "partenariats", label: "🤝 Partenariats" },
];

function emptyForm() {
  return {
    title: "", category: "financements", description: "", image: "", pdf_url: "",
    link_external: "", deadline: "", location: "", organizer: "", contact_email: "",
    ticket_name: "", ticket_price: 0, ticket_currency: "eur", status: "published",
  };
}

export default function AdminOpportunities() {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") === "submissions" ? "submissions" : "published");
  const [items, setItems] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [appsOpen, setAppsOpen] = useState(false);
  const [apps, setApps] = useState([]);
  const [currentOpp, setCurrentOpp] = useState(null);

  const load = useCallback(() => api.get("/admin/opportunities").then((r) => setItems(r.data || [])).catch(() => {}), []);
  const loadSubs = useCallback(() => api.get("/admin/opportunity-submissions").then((r) => setSubmissions(r.data || [])).catch(() => {}), []);
  useEffect(() => { load(); loadSubs(); }, [load, loadSubs]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { setOpen(false); setAppsOpen(false); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const openNew = () => { setEdit(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (o) => { setEdit(o); setForm({ ...emptyForm(), ...o }); setOpen(true); };
  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, ticket_price: parseFloat(form.ticket_price) || 0 };
      if (edit) await api.patch(`/admin/opportunities/${edit.id}`, payload);
      else await api.post("/admin/opportunities", payload);
      toast.success(edit ? "Opportunité mise à jour" : "Opportunité créée");
      setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const remove = async (o) => {
    if (!window.confirm(`Archiver "${o.title}" ?`)) return;
    await api.delete(`/admin/opportunities/${o.id}`); toast.success("Archivée"); load();
  };
  const openApps = async (o) => {
    setCurrentOpp(o);
    try { const r = await api.get(`/admin/opportunities/${o.id}/applications`); setApps(r.data || []); setAppsOpen(true); }
    catch { toast.error("Erreur chargement candidatures"); }
  };

  const approveSubmission = async (s) => {
    if (!window.confirm(`Approuver la soumission « ${s.title} » ?\n\nElle sera créée en brouillon dans Opportunités. Vous pourrez la finaliser et la publier.`)) return;
    try { await api.post(`/admin/opportunity-submissions/${s.id}/approve`); toast.success("Soumission approuvée — opportunité créée en brouillon"); loadSubs(); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const rejectSubmission = async (s) => {
    const reason = window.prompt("Motif du rejet (optionnel — sera envoyé par email au soumissionnaire) :", "");
    if (reason === null) return;
    try { await api.post(`/admin/opportunity-submissions/${s.id}/reject`, { reason }); toast.success("Soumission rejetée"); loadSubs(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const deleteSubmission = async (s) => {
    if (!window.confirm(`Supprimer définitivement la soumission « ${s.title} » ?`)) return;
    try { await api.delete(`/admin/opportunity-submissions/${s.id}`); toast.success("Soumission supprimée"); loadSubs(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  return (
    <PortalLayout sidebar="admin">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-navy md:text-4xl"><Rocket className="mr-2 inline h-7 w-7 text-wine-500" />Opportunités</h1>
          <p className="mt-1 text-neutral-600">Publiez et suivez vos appels à projets, programmes d&apos;accélération, challenges, missions export, appels d&apos;offres et recherches de partenaires.</p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 rounded-full bg-wine-500 px-5 py-2.5 text-sm font-bold text-white hover:bg-wine-600" data-testid="opportunity-new-btn">
          <Plus className="h-4 w-4" /> Nouvelle opportunité
        </button>
      </div>

      <div className="mt-6 flex gap-1 border-b border-neutral-200">
        <button onClick={() => setTab("published")} className={`relative px-4 py-2.5 text-sm font-semibold ${tab === "published" ? "text-wine-700" : "text-neutral-500 hover:text-navy"}`} data-testid="tab-published">
          <FileText className="mr-1.5 inline h-4 w-4" /> Publiées ({items.length})
          {tab === "published" && <span className="absolute bottom-0 left-0 h-0.5 w-full bg-wine-500" />}
        </button>
        <button onClick={() => setTab("submissions")} className={`relative px-4 py-2.5 text-sm font-semibold ${tab === "submissions" ? "text-wine-700" : "text-neutral-500 hover:text-navy"}`} data-testid="tab-submissions">
          <Inbox className="mr-1.5 inline h-4 w-4" /> Soumissions communauté
          {submissions.filter((s) => s.status === "pending").length > 0 && (
            <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-wine-500 px-2 py-0.5 text-[10px] font-bold text-white">{submissions.filter((s) => s.status === "pending").length}</span>
          )}
          {tab === "submissions" && <span className="absolute bottom-0 left-0 h-0.5 w-full bg-wine-500" />}
        </button>
      </div>

      {tab === "submissions" && (
        <div className="mt-6 overflow-x-auto rounded-2xl border border-neutral-100 bg-white">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="bg-wine-50/40 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-4 py-3 text-left">Titre</th>
                <th className="px-4 py-3 text-left">Catégorie</th>
                <th className="px-4 py-3 text-left">Soumis par</th>
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Statut</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {submissions.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-neutral-500">Aucune soumission pour le moment.</td></tr>}
              {submissions.map((s) => (
                <tr key={s.id} className="border-t border-neutral-100" data-testid={`submission-row-${s.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-navy">{s.title}</div>
                    <div className="mt-0.5 line-clamp-1 text-xs text-neutral-500">{s.description}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">{CATEGORY_OPTIONS.find((c) => c.value === s.category)?.label || s.category}</td>
                  <td className="px-4 py-3 text-xs">
                    <div className="font-semibold">{s.contact_name}</div>
                    <div className="text-neutral-500">{s.contact_email}</div>
                    {s.organization && <div className="text-neutral-400 italic">{s.organization}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs text-neutral-500">{(s.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-3">
                    {s.status === "pending" && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700">⏳ En attente</span>}
                    {s.status === "approved" && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">✅ Approuvée</span>}
                    {s.status === "rejected" && <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-bold text-rose-700">❌ Rejetée</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {s.status === "pending" && (
                        <>
                          <button onClick={() => approveSubmission(s)} className="grid h-7 w-7 place-items-center rounded-full text-emerald-600 hover:bg-emerald-50" title="Approuver" data-testid={`sub-approve-${s.id}`}><CheckCircle2 className="h-3.5 w-3.5" /></button>
                          <button onClick={() => rejectSubmission(s)} className="grid h-7 w-7 place-items-center rounded-full text-rose-600 hover:bg-rose-50" title="Rejeter" data-testid={`sub-reject-${s.id}`}><XCircle className="h-3.5 w-3.5" /></button>
                        </>
                      )}
                      {s.url && <a href={s.url} target="_blank" rel="noreferrer" className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-neutral-100" title="Lien"><ExternalLink className="h-3.5 w-3.5" /></a>}
                      <button onClick={() => deleteSubmission(s)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-400 hover:bg-rose-50 hover:text-rose-600" title="Supprimer" data-testid={`sub-del-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "published" && (
      <div className="mt-6 overflow-x-auto rounded-2xl border border-neutral-100 bg-white">
        <table className="w-full min-w-[800px] text-sm">
          <thead className="bg-wine-50/40 text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="px-4 py-3 text-left">Titre</th>
              <th className="px-4 py-3 text-left">Rubrique</th>
              <th className="px-4 py-3 text-left">Deadline</th>
              <th className="px-4 py-3 text-left">Prix</th>
              <th className="px-4 py-3 text-left">Statut</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-neutral-500">Aucune opportunité — créez-en une pour démarrer.</td></tr>}
            {items.map((o) => (
              <tr key={o.id} className="border-t border-neutral-100" data-testid={`opportunity-row-${o.id}`}>
                <td className="px-4 py-3">
                  <a href={`/opportunites/${o.slug}`} target="_blank" rel="noreferrer" className="font-semibold text-navy hover:text-wine-700">{o.title}</a>
                </td>
                <td className="px-4 py-3 text-xs">{CATEGORY_OPTIONS.find((c) => c.value === o.category)?.label || o.category}</td>
                <td className="px-4 py-3 text-xs">{(o.deadline || "").slice(0, 10) || "—"}</td>
                <td className="px-4 py-3 text-xs">{(o.ticket_price || 0) > 0 ? `${o.ticket_price} €` : "Gratuit"}</td>
                <td className="px-4 py-3">
                  {o.status === "published" ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">Publiée</span> : <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-bold text-neutral-600">Brouillon</span>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openApps(o)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-blue-50 hover:text-blue-700" title="Candidatures" data-testid={`opportunity-apps-${o.id}`}><Users className="h-3.5 w-3.5" /></button>
                    <a href={`/opportunites/${o.slug}`} target="_blank" rel="noreferrer" className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-blue-50 hover:text-blue-700" title="Voir public" data-testid={`opportunity-view-${o.id}`}><Eye className="h-3.5 w-3.5" /></a>
                    <button onClick={() => openEdit(o)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-blue-50 hover:text-blue-700" title="Éditer" data-testid={`opportunity-edit-${o.id}`}><Edit3 className="h-3.5 w-3.5" /></button>
                    <button onClick={() => remove(o)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-rose-50 hover:text-rose-700" title="Archiver" data-testid={`opportunity-del-${o.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setOpen(false)}>
          <form onSubmit={save} className="w-full max-w-3xl rounded-2xl bg-white p-6 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="opportunity-modal">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-navy">{edit ? "Modifier opportunité" : "Nouvelle opportunité"}</h3>
              <button type="button" onClick={() => setOpen(false)} className="text-neutral-400"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titre" className="md:col-span-2 rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="opp-title" />
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="opp-category">
                {CATEGORY_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="opp-status">
                <option value="published">Publiée</option>
                <option value="draft">Brouillon</option>
              </select>
              <div className="md:col-span-2">
                <label className="text-[10px] font-bold uppercase text-neutral-500">Image de couverture</label>
                <ImageUpload value={form.image} onChange={(url) => setForm({ ...form, image: url })} />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] font-bold uppercase text-neutral-500">Description</label>
                <MarkdownEditor value={form.description} onChange={(v) => setForm({ ...form, description: v })} rows={6} testId="opp-desc" />
              </div>
              <input value={form.pdf_url} onChange={(e) => setForm({ ...form, pdf_url: e.target.value })} placeholder="URL PDF dossier (optionnel)" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="opp-pdf" />
              <input value={form.link_external} onChange={(e) => setForm({ ...form, link_external: e.target.value })} placeholder="Lien externe (optionnel)" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="opp-link" />
              <input type="date" value={form.deadline ? form.deadline.slice(0, 10) : ""} onChange={(e) => setForm({ ...form, deadline: e.target.value })} placeholder="Deadline" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="opp-deadline" />
              <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Lieu (optionnel)" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="opp-location" />
              <input value={form.organizer} onChange={(e) => setForm({ ...form, organizer: e.target.value })} placeholder="Organisateur" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="opp-organizer" />
              <input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="Email de contact" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="opp-contact" />

              <div className="md:col-span-2 rounded-xl border border-amber-200 bg-amber-50/40 p-4">
                <div className="text-[10px] font-bold uppercase text-neutral-500">Ticket / Paiement (optionnel)</div>
                <div className="mt-2 grid gap-2 md:grid-cols-2">
                  <input value={form.ticket_name} onChange={(e) => setForm({ ...form, ticket_name: e.target.value })} placeholder="Nom du ticket (ex: Frais de dossier)" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="opp-ticket-name" />
                  <div className="flex items-center gap-2">
                    <input type="number" min="0" step="0.01" value={form.ticket_price} onChange={(e) => setForm({ ...form, ticket_price: parseFloat(e.target.value) || 0 })} placeholder="0" className="flex-1 rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="opp-ticket-price" />
                    <select value={form.ticket_currency} onChange={(e) => setForm({ ...form, ticket_currency: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm">
                      <option value="eur">€ EUR</option>
                      <option value="usd">$ USD</option>
                      <option value="gbp">£ GBP</option>
                    </select>
                  </div>
                </div>
                <p className="mt-2 text-[11px] text-neutral-500">💡 Laissez le prix à 0 pour une candidature gratuite. Si &gt; 0, Stripe générera automatiquement un lien de paiement + reçu + facture.</p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="rounded-full border border-neutral-300 px-4 py-2 text-sm">Annuler</button>
              <button type="submit" className="rounded-full bg-wine-500 px-5 py-2 text-sm font-bold text-white hover:bg-wine-600" data-testid="opp-save">{edit ? "Mettre à jour" : "Créer"}</button>
            </div>
          </form>
        </div>
      )}

      {appsOpen && currentOpp && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setAppsOpen(false)}>
          <div className="w-full max-w-4xl rounded-2xl bg-white p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="opportunity-apps-modal">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-navy">{apps.length} candidature{apps.length > 1 ? "s" : ""} — {currentOpp.title}</h3>
              <button onClick={() => setAppsOpen(false)} className="text-neutral-400"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-4 overflow-x-auto rounded-xl border border-neutral-100">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                  <tr><th className="px-3 py-2 text-left">Nom</th><th className="px-3 py-2 text-left">Email</th><th className="px-3 py-2 text-left">Entreprise</th><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Paiement</th></tr>
                </thead>
                <tbody>
                  {apps.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-neutral-400">Aucune candidature pour le moment.</td></tr>}
                  {apps.map((a) => (
                    <tr key={a.id} className="border-t border-neutral-100" data-testid={`app-row-${a.id}`}>
                      <td className="px-3 py-2">{a.first_name} {a.last_name}</td>
                      <td className="px-3 py-2 text-xs">{a.email}</td>
                      <td className="px-3 py-2 text-xs">{a.company || "—"}</td>
                      <td className="px-3 py-2 text-xs">{(a.created_at || "").slice(0, 10)}</td>
                      <td className="px-3 py-2 text-xs">
                        {a.payment_status === "free" && <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px]">Gratuit</span>}
                        {a.payment_status === "pending" && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700">En attente paiement</span>}
                        {a.payment_status === "paid" && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">Payée</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </PortalLayout>
  );
}
